import farms from 'config/constants/farms'
import { ecoFarm } from 'config/constants/ecoBoost'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import {
  CHAIN_TYPE,
  CollectionParams,
  useNftsAssetInfo,
  UserNftCollectionsMeta,
  useUserAllNftCollections,
  useUserNftCollections,
} from 'utils/calls'
import { isEcoFarmSupported } from 'utils/isNftStakingSupported'
import { UseQueryOptions } from 'react-query'
import _ from 'lodash'

const useGetUserCollectionParams = (farmId: number): CollectionParams => {
  const { account } = useActiveWeb3React()

  const currentFarm = farms.find((farm) => farm.pid === farmId)
  const nftCollectionAddress = ecoFarm[currentFarm?.ecoFarm]?.collectionAddress
  const nftCollectionChainType = ecoFarm[currentFarm?.ecoFarm]?.chain
  const userCollectionParams: CollectionParams = {
    address: account,
    filter: {
      chain: nftCollectionChainType,
      collections: [nftCollectionAddress],
      listingType: 'ALL',
    },
  }

  return userCollectionParams
}

export const useGetEcoFarmNftCollections = (
  farmId: number,
  queryOptions?: Omit<
    UseQueryOptions<UserNftCollectionsMeta, unknown, UserNftCollectionsMeta, any[]>,
    'queryFn' | 'queryKey'
  >,
) => {
  const isEcoFarm = isEcoFarmSupported(farmId)
  const userCollectionParams = useGetUserCollectionParams(farmId)

  const { data: userNftCollections, isLoading: isUserNftCollectionsLoading } = useUserNftCollections(
    userCollectionParams,
    {
      enabled: isEcoFarm,
      ...queryOptions,
    },
  )

  return {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
  }
}

export const useGetEcoFarmAllNftCollections = (
  farmId: number,
  queryOptions?: Omit<
    UseQueryOptions<UserNftCollectionsMeta, unknown, UserNftCollectionsMeta, any[]>,
    'queryFn' | 'queryKey'
  >,
) => {
  const isEcoFarm = isEcoFarmSupported(farmId)
  const userCollectionParams = useGetUserCollectionParams(farmId)

  const {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetchingNextPage,
    isFetching,
  } = useUserAllNftCollections(userCollectionParams, {
    enabled: isEcoFarm,
    ...queryOptions,
  })

  return {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  }
}

export const useFormatLayerForNftDetails = (farmId: number) => {
  const {
    data: userAllNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  } = useGetEcoFarmAllNftCollections(farmId)

  const allEdges = _.flatten(
    userAllNftCollections?.pages.map((page) => {
      const { edges } = page
      return edges ?? []
    }) ?? [],
  )

  const formattedNftList = allEdges?.map(({ node }) => {
    return {
      id: node.tokenId,
      imgUrl: node?.image?.url,
      name: node.name,
      attributes: [],
    }
  })

  return {
    data: formattedNftList,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  }
}

export const useFormatLayerForNftsDetails = (
  nftAddress: string,
  tokenIds: string[],
  farmId: number,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const isEcoFarm = isEcoFarmSupported(farmId)

  const results = useNftsAssetInfo(
    {
      nftAddress,
      chain: CHAIN_TYPE.CRONOS,
      tokenIds,
    },
    {
      enabled: isEcoFarm && (queryOptions?.enabled ?? true),
      ...queryOptions,
    },
  )

  let loadingCount = 0

  const formattedResults = results.map((result) => {
    if (result?.isLoading) {
      loadingCount += 1
    }
    return {
      id: result?.data?.tokenId,
      name: result?.data?.name,
      imgUrl: result?.data?.image?.url,
      attributes: [],
    }
  })

  return {
    data: formattedResults,
    isLoading: loadingCount !== 0,
  }
}

export default useGetEcoFarmNftCollections
